import { useEffect } from "react";

/**
 * Kort som kommer til syne når man ruller ned til dem.
 *
 * Siden er lang — sju rutekort, et skjema, et kart, en tidslinje, og et titalls
 * paneler under den. Alt sto ferdig tegnet fra første bilde, og en side som
 * viser alt på én gang gir ingen følelse av at det er noe å oppdage lenger nede.
 *
 * Tre valg er verdt å begrunne:
 *
 *   Klassen legges på fra JavaScript, ikke i oppmerkingen. Et kort som starter
 *   gjennomsiktig i CSS og venter på at et skript skal gjøre det synlig, er et
 *   kort som blir borte for alle som ikke får kjørt skriptet. Her er
 *   utgangspunktet synlig, og skjulingen skjer først i det øyeblikket vi vet at
 *   vi også kan vise det igjen.
 *
 *   `IntersectionObserver` framfor en rullelytter. Nettleseren vet allerede hva
 *   som er i bildet; å regne det ut på nytt for hvert bilde ville vært å gjøre
 *   den jobben en gang til, dårligere.
 *
 *   Hvert kort slutter å bli observert i det det er vist. Animasjonen skal skje
 *   én gang. Et kort som toner inn på nytt hver gang man ruller forbi det, blir
 *   irriterende ved andre gjennomlesning — og det er nettopp da man leter etter
 *   noe bestemt.
 */

/** Hvor mye av kortet som må være inne før det regnes som sett. */
const THRESHOLD = 0.06;

/**
 * Observerer kortene på siden og viser dem etter hvert.
 *
 * `deps` er tingene som får nye kort til å dukke opp — en ny plan, et bytte av
 * språk. Effekten kjører på nytt og fanger opp det som har kommet til; kort som
 * alt er vist, står urørt.
 */
export function useScrollReveal(deps: readonly unknown[]): void {
  useEffect(() => {
    /*
     * Den som har slått av bevegelse får siden som den er. Ingen skjuling, og
     * dermed heller ingenting som må vises igjen.
     */
    const still =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (still) return;

    // jsdom har ingen IntersectionObserver, og en server har ingen DOM. Uten
    // vakten ville kortene blitt skjult av en observatør som aldri melder fra.
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add("reveal--in");
          observer.unobserve(entry.target);
        }
      },
      // Litt inn i bildet, ikke helt i kanten: et kort som toner inn mens det
      // så vidt stikker opp nederst, rekker aldri å bli sett tone inn.
      { threshold: THRESHOLD, rootMargin: "0px 0px -6% 0px" },
    );

    /*
     * `:not(.reveal--in)`, ikke `:not(.reveal)`.
     *
     * Dette var en ekte feil, og en som ingen test fanget: jsdom har ingen
     * IntersectionObserver, så hele kroken hopper av i testene.
     *
     * Med `:not(.reveal)` ble et kort som var merket, men ennå ikke vist,
     * hoppet over neste gang effekten kjørte — og den kjører hver gang det
     * kommer nye kort på siden. Den gamle observatøren var da alt koblet fra i
     * opprydningen, så ingen fulgte med på kortet lenger. Det ble stående på
     * `opacity: 0` for alltid. I praksis: man valgte en rute, siden rullet ned
     * til skjemaet, og skjermen var tom.
     *
     * Å velge på «er den vist» framfor «er den merket» gjør gjenopptakelsen
     * riktig: alt som ennå ikke er vist, blir observert på nytt.
     */
    const cards = document.querySelectorAll<HTMLElement>(".card:not(.reveal--in)");
    for (const card of cards) {
      card.classList.add("reveal");
      observer.observe(card);
    }

    return () => observer.disconnect();
    // Kalleren bestemmer hva som utløser en ny gjennomgang.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
